import { yupResolver } from '@hookform/resolvers/yup';
import logoImage from 'assets/brand/icon-cyberproof.png';
import RedirectIcon from 'assets/icons/redirect.svg?react';
import SelectedIcon from 'assets/icons/selected.svg?react';
import { Block, Button, Divider, Input, Select, SelectOption } from 'reablocks';
import { FC, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { VendorLogo } from 'shared/utils/VendorLogo';
import { useUserContext } from 'shared/user/UserContext';
import * as Yup from 'yup';
import css from './FreeTrial.module.css';
import {
  COUNTRIES,
  INDUSTRIES
} from '../Settings/ThreatProfiles/EditProfile/mappings';
import classNames from 'classnames';
import ThreatLandscape from './ThreatLandscape';

// Simple platforms list
const PLATFORMS = [
  'Windows',
  'Linux',
  'macOS',
  'IaaS',
  'SaaS',
  'Containers',
  'Network'
];

const personalDomains = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'protonmail.com',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'gmx.com',
  'live.com',
  'msn.com',
  'me.com',
  'inbox.com',
  '10minutemail.com',
  'temp-mail.org',
  'guerrillamail.com',
  'mailinator.com',
  'yopmail.com',
  'tempmail.net',
  'sharklasers.com',
  'dispostable.com',
  'fakeinbox.com',
  'throwawaymail.com',
  'test.com'
];

// Security product icons mapping (same as in FreeTrial component)
export const SECURITY_PRODUCTS = [
  'Generic Endpoint Product',
  'CrowdStrike Falcon',
  'Microsoft Defender',
  'PaloAlto Cortex XDR',
  'SentinelOne Singularity',
  'AhnLab EDR',
  'Bitdefender GravityZone',
  'Carbon Black',
  'Cisco Secure Endpoint',
  'CyCraft-Xensor',
  'Cybereason XDR',
  'Cylance',
  'Cynet 360',
  'ESET Inspect',
  'ElasticSearch',
  'Fidelis Endpoint',
  'FireEye Endpoint Security',
  'FortiEDR',
  'Harmony Endpoint',
  'Kaspersky Endpoint Security',
  'Malwarebytes Endpoint',
  'McAfee Endpoint Protection',
  'MicroFocus ArcSight ESM',
  'OpenText Endpoint Security',
  'Secureworks Taegis XDR',
  'Sophos Intercept X with XDR',
  'Symantec Endpoint Security',
  'TrendMicro Vision One',
  'Uptycs'
];

// Helper function to break text at slashes and hyphens for better display
const breakText = text =>
  text?.replaceAll('/', '/<wbr>')?.replaceAll('-', '-<wbr>');

const checkIfFakeDomain = async domain => {
  try {
    const response = await fetch(
      `https://fakefilter.net/api/is/fakedomain/${domain}`
    );

    if (!response.ok) {
      // Fail open - if we get an error, assume the domain is real
      console.warn(
        `FakeFilter API error: ${response.status} for domain ${domain}`
      );
      return false;
    }

    const data = await response.json();
    return data.isFakeDomain !== false;
  } catch (error) {
    // Fail open - if there's an error with the request, assume domain is real
    console.error(`Error checking domain ${domain}:`, error);
    return false;
  }
};

// Only schema for step 1 basic fields
const step1Schema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Required')
    .test(
      'is-real-email',
      'Please use your work email',
      async function (value) {
        if (!value) return true;

        // First check that is is a valid email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return true;
        }

        const domain = value.split('@')[1];

        // Check if domain is in personal domains list
        if (personalDomains.includes(domain)) {
          return false;
        }

        // Only call FakeFilter API if we have a valid email format
        const isFake = await checkIfFakeDomain(domain);
        if (isFake) {
          return false;
        }

        return true;
      }
    ),
  fullName: Yup.string().required('Required'),
  companyName: Yup.string().required('Required')
});

interface FreeTrialProps {
  onDiscover: (value: {
    email: string;
    fullName: string;
    companyName: string;
    countries?: string[];
    industries?: string[];
    platforms?: string[];
    products?: string[];
  }) => void;
}
/**
 * A "domain registration" represents a single tenancy of a user.  For instance,
 * if a user belonged to organization A and B, the API would return two
 * registrations for them (one for org A and one for B).
 */
export interface DomainRegistration {
  client_id: string;
  tenant_id: string;
  tenant_name: string;
}
/** Gets the possible tenants for the provided email address */
export const fetchTenants = async (
  email: string
): Promise<DomainRegistration[]> => {
  const response = await fetch('/auth/v2/client', {
    body: JSON.stringify({ email }),
    method: 'POST'
  });
  const { domain_registrations } = await response.json();
  return domain_registrations;
};

const errorCodes = {
  '1': 'Your IP address is not on the tenant allow list. Please contact support.'
};

/**
 * Gets referrer information with a priority order:
 * 1. document.referrer
 * 2. localStorage.lastReferrer
 * 3. URL ref parameter (sanitized)
 *
 * @returns String containing referrer source
 */
const getReferralSource = (): string => {
  // Check URL parameters for ref
  const urlParams = new URLSearchParams(window.location.search);
  const refParam = urlParams.get('ref');

  // Determine referrer with priority order
  let referrer = 'direct';

  // 1. Try document.referrer
  if (document.referrer) {
    referrer = document.referrer;
    // Store in localStorage for future visits
    localStorage.setItem('lastReferrer', document.referrer);
  }
  // 2. Try localStorage if document.referrer is not available
  else if (localStorage.getItem('lastReferrer')) {
    referrer = localStorage.getItem('lastReferrer') || 'direct';
  }
  // 3. Try URL ref parameter as last resort
  else if (refParam) {
    referrer = refParam || 'direct';
  }

  return referrer;
};

/**
 * Gets Google Ads UTM parameters from URL
 * @returns Object containing UTM parameters
 */
const getUtmParameters = () => {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_content: urlParams.get('utm_content'), // Ad Group
    utm_term: urlParams.get('utm_term'), // Keyword
    // Additional UTM parameters that might be used
    utm_id: urlParams.get('utm_id'), // Campaign ID
    utm_source_platform: urlParams.get('utm_source_platform'), // Source platform
    gclid: urlParams.get('gclid'), // Google Click ID
    fbclid: urlParams.get('fbclid'), // Facebook Click ID
    msclkid: urlParams.get('msclkid') // Microsoft Click ID
  };
};

const FreeTrial: FC<FreeTrialProps> = ({ onDiscover }) => {
  interface FormData {
    email: string;
    fullName: string;
    companyName: string;
    countries: string[];
    industries: string[];
    platforms: string[];
    products: string[];
  }

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    fullName: '',
    companyName: '',
    countries: [],
    industries: [],
    platforms: [],
    products: []
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { isSubmitting, isValid, errors }
  } = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: currentStep === 1 ? yupResolver(step1Schema) : undefined,
    defaultValues: {
      email: '',
      fullName: '',
      companyName: '',
      countries: [],
      industries: [],
      platforms: [],
      products: []
    }
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const { isAuthenticated } = useUserContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isGeneratingAssessment, setIsGeneratingAssessment] = useState(false);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const [assessmentDownloadUrl, setAssessmentDownloadUrl] = useState<
    string | null
  >(null);
  const [assessmentFileName, setAssessmentFileName] = useState<string>('');
  const [step2Errors, setStep2Errors] = useState<{
    industries?: string;
    countries?: string;
    platforms?: string;
    products?: string;
  }>({});

  // Watch form values for real-time updates
  const watchedIndustries = useWatch({
    control,
    name: 'industries',
    defaultValue: formData.industries
  });
  const watchedCountries = useWatch({
    control,
    name: 'countries',
    defaultValue: formData.countries
  });
  const watchedPlatforms = useWatch({
    control,
    name: 'platforms',
    defaultValue: formData.platforms
  });
  const watchedProducts = useWatch({
    control,
    name: 'products',
    defaultValue: formData.products
  });

  // Watch for form changes to track unsaved changes
  const watchedEmail = useWatch({ control, name: 'email' });
  const watchedFullName = useWatch({ control, name: 'fullName' });
  const watchedCompanyName = useWatch({ control, name: 'companyName' });

  // Track if form has unsaved changes
  useEffect(() => {
    const hasChanges = !!(
      watchedEmail ||
      watchedFullName ||
      watchedCompanyName ||
      watchedIndustries?.length ||
      watchedCountries?.length ||
      watchedPlatforms?.length ||
      watchedProducts?.length
    );
    setHasUnsavedChanges(hasChanges && !isSubmitted);
  }, [
    watchedEmail,
    watchedFullName,
    watchedCompanyName,
    watchedIndustries,
    watchedCountries,
    watchedPlatforms,
    watchedProducts,
    isSubmitted
  ]);

  // Add beforeunload event listener
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        const message =
          'You have unsaved changes to your threat profile assessment. Are you sure you want to leave?';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Cleanup assessment download URL when component unmounts
  useEffect(() => {
    return () => {
      if (assessmentDownloadUrl) {
        window.URL.revokeObjectURL(assessmentDownloadUrl);
      }
    };
  }, [assessmentDownloadUrl]);

  // Filter security products based on search term
  const filteredProducts = SECURITY_PRODUCTS.filter(product =>
    product.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle step 1 form submission (continue to threat profile)
  const onContinueToThreatProfile = async (data: FormData) => {
    // Validate basic info fields manually for step 1
    const isEmailValid = data.email && data.email.trim() !== '';
    const isNameValid = data.fullName && data.fullName.trim() !== '';
    const isCompanyValid = data.companyName && data.companyName.trim() !== '';

    if (!isEmailValid || !isNameValid || !isCompanyValid) {
      return; // Let form validation handle the errors
    }

    // Log customer data for follow-up purposes
    try {
      const referrer = getReferralSource();
      const utmParams = getUtmParameters();

      fetch('/auth/pre-free-trial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: data.email,
          full_name: data.fullName,
          company_name: data.companyName,
          referrer: referrer,
          ...utmParams
        })
      });
    } catch (error) {
      console.log('Pre-trial logging failed:', error);
    }

    setFormData(prev => ({
      ...prev,
      email: data.email,
      fullName: data.fullName,
      companyName: data.companyName
    }));
    setCurrentStep(2);

    // Reset form for step 2 to ensure clean state
    reset({
      email: data.email,
      fullName: data.fullName,
      companyName: data.companyName,
      countries: formData.countries,
      industries: formData.industries,
      platforms: formData.platforms,
      products: formData.products
    });
  };

  // Validate step 2 fields manually
  const validateStep2 = (data: FormData) => {
    const errors: typeof step2Errors = {};

    if (!data.industries || data.industries.length === 0) {
      errors.industries = 'Please select at least one industry';
    }
    if (!data.countries || data.countries.length === 0) {
      errors.countries = 'Please select at least one country';
    }
    if (!data.platforms || data.platforms.length === 0) {
      errors.platforms = 'Please select at least one platform';
    }
    if (!data.products || data.products.length === 0) {
      errors.products = 'Please select at least one product';
    }

    setStep2Errors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle final form submission
  const onSubmit = async (data: FormData) => {
    const finalData = { ...formData, ...data };

    // Validate step 2 fields manually
    if (!validateStep2(finalData)) {
      return;
    }

    try {
      const referrer = getReferralSource();
      const utmParams = getUtmParameters();

      const response = await fetch('/auth/free-trial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: finalData.email,
          full_name: finalData.fullName,
          company_name: finalData.companyName,
          countries: finalData.countries,
          industries: finalData.industries,
          platforms: finalData.platforms,
          products: finalData.products,
          referrer: referrer,
          ...utmParams
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit');
      }

      onDiscover(finalData);
      setIsSubmitted(true);
      setHasUnsavedChanges(false); // Clear unsaved changes flag

      // Start generating assessment PDF
      setIsGeneratingAssessment(true);

      try {
        const assessmentResponse = await fetch('/api/rest/v1/assessment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: finalData.email,
            full_name: finalData.fullName,
            company_name: finalData.companyName,
            countries: finalData.countries,
            industries: finalData.industries,
            platforms: finalData.platforms,
            products: finalData.products,
            referrer: referrer
          })
        });

        if (assessmentResponse.ok) {
          // Get the PDF blob
          const pdfBlob = await assessmentResponse.blob();

          // Create a blob URL and trigger download
          const url = window.URL.createObjectURL(pdfBlob);
          const fileName = `threat-assessment-${finalData.companyName.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;

          // Store the URL and filename for the backup download link
          setAssessmentDownloadUrl(url);
          setAssessmentFileName(fileName);

          // Attempt automatic download
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Note: We don't revoke the URL here anymore since we need it for the backup link
        } else {
          console.error('Failed to generate assessment PDF');
        }
      } catch (assessmentError) {
        console.error('Error generating assessment:', assessmentError);
      } finally {
        setIsGeneratingAssessment(false);
        setAssessmentCompleted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // Clear step 2 errors when values change
  useEffect(() => {
    if (currentStep === 2) {
      const currentData = getValues();
      const newErrors = { ...step2Errors };

      if (currentData.industries?.length > 0) delete newErrors.industries;
      if (currentData.countries?.length > 0) delete newErrors.countries;
      if (currentData.platforms?.length > 0) delete newErrors.platforms;
      if (currentData.products?.length > 0) delete newErrors.products;

      setStep2Errors(newErrors);
    }
  }, [
    watchedIndustries,
    watchedCountries,
    watchedPlatforms,
    watchedProducts,
    currentStep
  ]);

  // Go back to step 1
  const goBackToStep1 = () => {
    const currentData = getValues();
    const updatedFormData = { ...formData, ...currentData };
    setFormData(updatedFormData);
    setCurrentStep(1);

    // Force set the values explicitly to ensure they show up
    setTimeout(() => {
      setValue('fullName', updatedFormData.fullName || '');
      setValue('email', updatedFormData.email || '');
      setValue('companyName', updatedFormData.companyName || '');
      setValue('countries', updatedFormData.countries || []);
      setValue('industries', updatedFormData.industries || []);
      setValue('platforms', updatedFormData.platforms || []);
      setValue('products', updatedFormData.products || []);
    }, 0);
  };

  return (
    <>
      <Helmet>
        <title>Assessment & Free Trial</title>
      </Helmet>
      <div className={css.container}>
        <div className={css.left}>
          {!isSubmitted ? (
            <>
              {currentStep === 1 ? (
                <form onSubmit={handleSubmit(onContinueToThreatProfile)}>
                  <div className={css.login}>
                    <img
                      src={logoImage}
                      alt="CyberProof Logo"
                      className={css.logo}
                    />
                    <p className={classNames(css.tagline, css.upperTagline)}>
                      Begin prioritizing the threats that matter most!
                    </p>
                    <p className={css.tagline}>
                      Sign up now to receive a personalized threat landscape
                      assessment in seconds, plus unlock your free 30-day trial.
                    </p>
                    <Divider />
                    <Block
                      label="Name"
                      className={`${css.blockWidth} ${css.formField}`}
                    >
                      <Controller
                        name="fullName"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input {...field} type="text" />
                            {error && (
                              <div className="mt-1 text-sm text-red-500">
                                {error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Block>

                    <Block
                      label="Email"
                      className={`${css.blockWidth} ${css.formField}`}
                    >
                      <Controller
                        name="email"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input {...field} type="email" />
                            {error && (
                              <div className="mt-1 text-sm text-red-500">
                                {error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Block>

                    <Block
                      label="Company Name"
                      className={`${css.blockWidth} ${css.formField}`}
                    >
                      <Controller
                        name="companyName"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input {...field} type="text" />
                            {error && (
                              <div className="mt-1 text-sm text-red-500">
                                {error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Block>

                    <Button
                      type="submit"
                      color="primary"
                      className="mt-8"
                      disabled={isSubmitting}
                    >
                      Continue to Threat Profile
                    </Button>
                    <Divider />
                    {isAuthenticated ? (
                      <div className={css.loginMessage}>
                        Looks like you are already authenticated,{' '}
                        <a href="/ciso">click here</a> to go back to the
                        dashboard.
                      </div>
                    ) : (
                      <div className={css.loginMessage}>
                        Already have an account?{' '}
                        <a href="/login">Log in here</a>.
                      </div>
                    )}
                    <a
                      className={css.redirect}
                      href="https://www.cyberproof.com/"
                      target="_blank"
                    >
                      Visit Website <RedirectIcon />
                    </a>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className={css.login}>
                    <img
                      src={logoImage}
                      alt="CyberProof Logo"
                      className={css.logo}
                    />
                    <div className="mb-4 flex items-center">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={goBackToStep1}
                        className="mr-4"
                      >
                        ← Back
                      </Button>
                      <h2 className="text-lg font-semibold">
                        Threat Profile & Integrations
                      </h2>
                    </div>

                    <Block label="Industries" className={css.blockWidth}>
                      <Controller
                        name="industries"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              placeholder="Select industries..."
                              onOptionsChange={opts =>
                                setValue(
                                  'industries',
                                  (opts as any).map(o => o.value)
                                )
                              }
                              multiple
                              {...field}
                            >
                              {Object.keys(INDUSTRIES).map(key => (
                                <SelectOption key={key} value={key}>
                                  {INDUSTRIES[key]}
                                </SelectOption>
                              ))}
                            </Select>
                            {step2Errors.industries && (
                              <div className="mt-1 text-sm text-red-500">
                                {step2Errors.industries}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Block>

                    <Block label="Countries" className={css.blockWidth}>
                      <Controller
                        name="countries"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              placeholder="Select countries..."
                              onOptionsChange={opts =>
                                setValue(
                                  'countries',
                                  (opts as any).map(o => o.value)
                                )
                              }
                              multiple
                              {...field}
                            >
                              {Object.keys(COUNTRIES)
                                .sort((a, b) => {
                                  // Put USA first, then sort the rest alphabetically by country name
                                  if (a === 'USA') return -1;
                                  if (b === 'USA') return 1;
                                  return COUNTRIES[a].localeCompare(
                                    COUNTRIES[b]
                                  );
                                })
                                .map(key => (
                                  <SelectOption key={key} value={key}>
                                    {decodeURIComponent(
                                      JSON.parse(`"${COUNTRIES[key]}"`)
                                    )}
                                  </SelectOption>
                                ))}
                            </Select>
                            {step2Errors.countries && (
                              <div className="mt-1 text-sm text-red-500">
                                {step2Errors.countries}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Block>

                    <Block label="Platforms" className={css.blockWidth}>
                      <Controller
                        name="platforms"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              placeholder="Select platforms..."
                              onOptionsChange={opts =>
                                setValue(
                                  'platforms',
                                  (opts as any).map(o => o.value)
                                )
                              }
                              multiple
                              {...field}
                            >
                              {PLATFORMS.map(platform => (
                                <SelectOption key={platform} value={platform}>
                                  {platform}
                                </SelectOption>
                              ))}
                            </Select>
                            {step2Errors.platforms && (
                              <div className="mt-1 text-sm text-red-500">
                                {step2Errors.platforms}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Block>

                    <Block label="Products" className={css.blockWidth}>
                      <div className={css.productsSearch}>
                        <Input
                          type="text"
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <Controller
                        name="products"
                        control={control}
                        render={({ field }) => (
                          <>
                            {step2Errors.products && (
                              <div className="mb-2 mt-1 text-sm text-red-500">
                                {step2Errors.products}
                              </div>
                            )}
                            <div
                              className={css.productsList}
                              style={{
                                maxHeight: '140px',
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column'
                              }}
                            >
                              {filteredProducts.map(product => (
                                <div
                                  key={product}
                                  className={classNames(css.productItem, {
                                    [css.active]: field.value.includes(product)
                                  })}
                                  style={{ width: '100%' }}
                                  onClick={() => {
                                    const currentProducts = [...field.value];
                                    if (currentProducts.includes(product)) {
                                      setValue(
                                        'products',
                                        currentProducts.filter(
                                          p => p !== product
                                        )
                                      );
                                    } else {
                                      setValue('products', [
                                        ...currentProducts,
                                        product
                                      ]);
                                    }
                                  }}
                                >
                                  {field.value.includes(product) && (
                                    <SelectedIcon className={css.selected} />
                                  )}
                                  <div className={css.productIcon}>
                                    <VendorLogo
                                      vendor={product}
                                      alt={product}
                                      style={{ marginLeft: 8 }}
                                    />
                                  </div>
                                  <div
                                    className={css.productName}
                                    style={{ marginLeft: 16 }}
                                  >
                                    {product}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      />
                    </Block>

                    <Button
                      type="submit"
                      color="primary"
                      className="mt-10"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? 'Submitting...'
                        : 'See Your Free Assessment!'}
                    </Button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <div className="p-8 text-center">
              <h3 className="text-xl font-semibold text-green-600">
                Free Assessment and Trial Successfully Requested!
              </h3>
              <p className="mt-2">
                Look out for an email from @cyberproof.com with more
                information!
              </p>

              {/* Assessment download link - always visible when completed */}
              {assessmentCompleted && assessmentDownloadUrl && (
                <div className="mt-4 rounded-lg bg-blue-50 p-4 md:hidden">
                  <p className="mb-2 text-sm text-gray-700">
                    Your threat assessment is ready!
                  </p>
                  <a
                    href={assessmentDownloadUrl}
                    download={assessmentFileName}
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Download Your Assessment
                  </a>
                </div>
              )}

              {/* Mobile-specific assessment status */}
              <div className="mt-6 block md:hidden">
                {isGeneratingAssessment ? (
                  <div className="flex flex-col items-center">
                    <div className="mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                    <p className="text-sm text-gray-600">
                      Generating your personalized threat assessment...
                    </p>
                  </div>
                ) : assessmentCompleted ? (
                  <div className="flex flex-col items-center">
                    <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-green-600">
                      <svg
                        className="h-4 w-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600">
                      Your threat assessment has been generated!
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
        <div className={css.right}>
          <div className={css.rightContent}>
            {currentStep === 1 ? (
              <div className={css.step1Content}>
                <h3 className={css.rightTitle}>
                  Optimize Your Defenses Against the Threats That Matter Most
                </h3>
                <div className={css.rightParagraph}>
                  Ensure your cyber defenses are optimized and ready to defend
                  against the most relevant security threats targeting your
                  organization.
                </div>
                <img
                  src="/screenshot.png"
                  alt="Screenshot"
                  className={css.screenshot}
                />
              </div>
            ) : isSubmitted && isGeneratingAssessment ? (
              <div className="flex h-full flex-col items-center justify-center p-8">
                <div className="mb-4 h-16 w-16 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <h3 className="mb-2 text-lg font-semibold text-gray-100">
                  Generating Your Threat Assessment
                </h3>
                <p className="text-center text-gray-200">
                  Please wait while we create your personalized threat landscape
                  assessment...
                </p>
              </div>
            ) : isSubmitted && assessmentCompleted ? (
              <div className="flex h-full flex-col items-center justify-center p-8">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-600">
                  <svg
                    className="h-8 w-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-100">
                  Your Threat Assessment is Ready!
                </h3>
                <p className="mb-4 text-center text-gray-200">
                  Your personalized threat landscape assessment has been
                  generated successfully and should have downloaded
                  automatically. A copy will also be emailed to you shortly.
                </p>

                {/* Backup download link */}
                {assessmentDownloadUrl && (
                  <a
                    href={assessmentDownloadUrl}
                    download={assessmentFileName}
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Download Your Assessment
                  </a>
                )}
              </div>
            ) : (
              <div className={css.step2Content}>
                <ThreatLandscape
                  industries={watchedIndustries}
                  countries={watchedCountries}
                  platforms={watchedPlatforms}
                  products={watchedProducts}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FreeTrial;
